import { useApp } from '../store';
import { parseNotes, type Block, type Inline } from '../markdown';
import { splitMentions } from '../mentions';

/**
 * Notes as they were meant to read.
 *
 * This used to do ONE thing: turn #123 into a link. Everything else in the
 * text — bold, code, bullets — was printed as the characters that were typed,
 * so a note reading "**Priority:** critical" showed its asterisks, and the tab
 * offering to "Preview Markdown" previewed no markdown whatsoever.
 *
 * Linking is still the part that matters most, and it is not markdown at all:
 * these notes express dependencies in prose — "Queued behind #200" — and that
 * reference becoming a link is what makes the board navigable. Which is why
 * this renders the small set of things these notes really contain rather than
 * reaching for a markdown library. See src/markdown.ts for what, and why that.
 */

/**
 * The inline half, shared. Turns an Inline[] into nodes: #123 → a link, **bold**
 * → bold, `code` → code. Both the block renderer (IssueText) and the inline one
 * (IssueTextInline) use this, so the two can never disagree about what the small
 * note language means — the whole point of there being one renderer.
 */
function useRenderInline(tint?: { me: string }) {
  const issues = useApp((s) => s.issues);
  const openIssue = useApp((s) => s.openIssue);
  const profiles = useApp((s) => s.profiles);

  const byRef = new Map(issues.map((i) => [i.ref, i]));
  const ALIASES: Record<string, string> = { '#115': 'B1', '#117': 'B2' };
  // A sub-part (#187-A) belongs to its parent, which is where the work lives.
  const resolve = (ref: string) =>
    byRef.get(ref) ?? byRef.get(ALIASES[ref] ?? '') ?? byRef.get(ref.replace(/^(#\d+).*$/, '$1'));

  // A run of plain text. With `tint` on (the card feed, My Work) a stored
  // @address inside it is shown as the person's name, tinted — and the reader's
  // own name stands out — the same reading MentionInput's mirror gives while you
  // type. Off (a summary, a charter) there are no stored @addresses, so this is
  // just the text. Mentions tint INSIDE bold too, because renderInline recurses
  // through this for a bold run's parts.
  const renderText = (text: string, key: string): React.ReactNode => {
    if (!tint) return text;
    const me = tint.me.toLowerCase();
    return splitMentions(text, profiles).map((p, m) =>
      p.mention ? (
        <span key={`${key}-m${m}`} className={`mention ${p.mention === me ? 'is-me' : ''}`} title={p.mention}>
          {p.text}
        </span>
      ) : (
        <span key={`${key}-m${m}`}>{p.text}</span>
      )
    );
  };

  const renderInline = (parts: Inline[], keyPrefix: string): React.ReactNode[] =>
    parts.map((part, n) => {
      const key = `${keyPrefix}-${n}`;
      if (part.kind === 'text') return <span key={key}>{renderText(part.text, key)}</span>;
      if (part.kind === 'code')
        return (
          <code key={key} className="md-code">
            {part.text}
          </code>
        );
      if (part.kind === 'bold') return <strong key={key}>{renderInline(part.parts, key)}</strong>;
      const target = resolve(part.ref);
      // An unresolved reference stays as plain text rather than becoming a link
      // to nothing — and seeing that before saving is the point of the preview.
      if (!target) return <span key={key}>{part.ref}</span>;
      return (
        <button
          key={key}
          className="ref-link"
          title={target.title}
          onClick={(e) => {
            e.stopPropagation();
            openIssue(target.id);
          }}
        >
          {part.ref}
        </button>
      );
    });

  return renderInline;
}

export default function IssueText({ text, mentions }: { text: string; mentions?: string }) {
  const renderInline = useRenderInline(mentions !== undefined ? { me: mentions } : undefined);

  if (!text) return null;

  const renderBlock = (block: Block, n: number) => {
    const key = `b${n}`;
    if (block.kind === 'pre')
      return (
        <pre key={key} className="md-pre">
          {block.text}
        </pre>
      );
    if (block.kind === 'list') {
      const items = block.items.map((item, i) => (
        <li key={`${key}-${i}`}>{renderInline(item, `${key}-${i}`)}</li>
      ));
      return block.ordered ? (
        <ol key={key} className="md-list">
          {items}
        </ol>
      ) : (
        <ul key={key} className="md-list">
          {items}
        </ul>
      );
    }
    return (
      <p key={key} className="md-para">
        {renderInline(block.parts, key)}
      </p>
    );
  };

  return <>{parseNotes(text).map(renderBlock)}</>;
}

/**
 * The same note language, rendered INLINE — no block <p>/<ul> wrappers.
 *
 * For the cells that show a one-line note inside a span or a pill: an epic's
 * health reason, a card-row blocker, an epic's latest update. Those printed
 * `**bold**` and `#123` as the literal characters typed, because they dropped
 * the raw string into the DOM while the board's own card views had always
 * routed the SAME text through IssueText (CPY-F1). This closes that gap without
 * emitting block elements into an inline context, which would be invalid markup
 * and break the pill's layout. Blocks are joined with a space and list items
 * with commas — these are short prose cells, never documents.
 */
export function IssueTextInline({ text, mentions }: { text: string; mentions?: string }) {
  const renderInline = useRenderInline(mentions !== undefined ? { me: mentions } : undefined);

  if (!text) return null;

  return (
    <>
      {parseNotes(text).map((block, n) => {
        const key = `i${n}`;
        const sep = n > 0 ? ' ' : '';
        if (block.kind === 'pre')
          return (
            <span key={key}>
              {sep}
              <code className="md-code">{block.text}</code>
            </span>
          );
        if (block.kind === 'list')
          return (
            <span key={key}>
              {sep}
              {block.items.map((item, i) => (
                <span key={`${key}-${i}`}>
                  {i > 0 ? ', ' : ''}
                  {renderInline(item, `${key}-${i}`)}
                </span>
              ))}
            </span>
          );
        return (
          <span key={key}>
            {sep}
            {renderInline(block.parts, key)}
          </span>
        );
      })}
    </>
  );
}
